import { useGetQueryData } from "@/utils/react-query-hooks/general";

/**
 * Decides whether a given FilterModule instance renders the new (V3) filter UI.
 *
 * Self-managed tenants always get V3. Non-self-managed tenants can opt in via the
 * deployment config `_FiltersFramework.framework === 'auto-gen'` (see
 * deployment/defaults.js), which makes them behave like a self-managed tenant
 * wrt filters.
 *
 * The new UI's annotation filters are hierarchy-first (they need the
 * tags_subThemes_themes annotation hierarchy). So surfaces that render annotation
 * filters must pass `usesAnnoFilters: true` — those only get V3 when the hierarchy
 * actually exists. On a 'flexible' annotation tenant they fall back to the old UI
 * rather than rendering an empty annotation section. Doc-only surfaces are always
 * safe and don't need to pass anything.
 *
 * @param {{ usesAnnoFilters?: boolean }} [opts]
 */
export const useShowFilterModuleV3 = ({ usesAnnoFilters = false } = {}) => {
  const platformConfigs = useGetQueryData("platformConfigs");

  const isSelfManagedTenant =
    platformConfigs?.SELF_MANAGED_BASE_CONFIGS?.enable === true;

  // Opt-in for non-self-managed tenants. Self-managed is always opted in.
  const optedIntoNewFilters =
    isSelfManagedTenant ||
    platformConfigs?.deployment?._FiltersFramework?.framework === "auto-gen";

  // The new annotation filter UI needs the tags_subThemes_themes hierarchy.
  const hasAnnoHierarchy =
    isSelfManagedTenant ||
    platformConfigs?.deployment?._AnnotationFramework?.framework ===
      "tags_subThemes_themes";

  // Annotation surfaces fall back to the old UI when the hierarchy is absent.
  if (usesAnnoFilters && !hasAnnoHierarchy) {
    return { showFilterModuleV3: false };
  }

  return {
    showFilterModuleV3: optedIntoNewFilters
  };
};
